<template>
  <div class="lazy-icon-wrapper">
    <div v-if="loading" class="icon-skeleton">
      <div class="skeleton-placeholder"></div>
    </div>

    <div v-else-if="error" class="icon-error">
      <i class="ri-error-warning-line"></i>
    </div>

    <component
      v-else-if="loadedComponent"
      :is="loadedComponent"
      :class="iconClass"
      v-bind="$attrs"
    />

    <div v-else class="icon-fallback">
      <i :class="fallbackIcon"></i>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { componentMap } from '@/config/sidenavModules.js'

export default {
  name: 'LazyIcon',
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      required: true
    },
    fallbackIcon: {
      type: String,
      default: 'ri-apps-line'
    },
    iconClass: {
      type: String,
      default: 'sidenav-icon'
    },
    preload: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadedComponent: null,
      loading: false,
      error: false
    }
  },
  async mounted() {
    if (this.preload) {
      await this.loadComponent()
    }
  },
  watch: {
    name: {
      handler() {
        this.loadComponent()
      },
      immediate: true
    }
  },
  methods: {
    async loadComponent() {
      if (this.loadedComponent || this.loading) return
      
      try {
        this.loading = true
        this.error = false
        
        if (!componentMap[this.name]) {
          throw new Error(`Component ${this.name} not found in componentMap`)
        }
        
        const componentModule = await componentMap[this.name]()
        this.loadedComponent = markRaw(componentModule.default || componentModule)
        
      } catch (err) {
        console.warn(`Failed to load icon component: ${this.name}`, err)
        this.error = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.lazy-icon-wrapper {
  width: 1.5rem;
  height: 1.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.icon-skeleton {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skeleton-placeholder {
  width: 1.25rem;
  height: 1.25rem;
  background: var(--skeleton-icon-bg, rgba(148, 163, 184, 0.3));
  border-radius: 0.25rem;
  animation: skeleton-pulse 1.2s ease-in-out infinite;
}

.icon-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--error-color, #ef4444);
  font-size: 1rem;
}

.icon-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--fallback-color, #6b7280);
  font-size: 1rem;
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Modo oscuro */
body.dark-mode-active .skeleton-placeholder {
  background: rgba(100, 116, 139, 0.4);
}

body.dark-mode-active .icon-error {
  color: #f87171;
}

body.dark-mode-active .icon-fallback {
  color: #9ca3af;
}

/* Variables CSS */
:root {
  --skeleton-icon-bg: rgba(148, 163, 184, 0.3);
  --error-color: #ef4444;
  --fallback-color: #6b7280;
}

body.dark-mode-active {
  --skeleton-icon-bg: rgba(100, 116, 139, 0.4);
  --error-color: #f87171;
  --fallback-color: #9ca3af;
}
</style>